import { createAction, props } from '@ngrx/store';

export interface BaseFilterType {
  limit?: number;
  skip?: number;
  order?: string;
}
export interface BaseEntityType {
  id: string;
}

export class CaEntityActionsFactory<
  EntityType extends BaseEntityType,
  CreateType,
  UpdateType,
  FilterType extends BaseFilterType
> {
  // Create
  private get REQUEST_CREATE() {
    return `[${this.type}][Request] Create`;
  }

  private get RESPONSE_SUCCESS_CREATE() {
    return `[${this.type}][Response][Success] Create`;
  }

  private get RESPONSE_FAIL_CREATE() {
    return `[${this.type}][Response][Fail] Create`;
  }

  // Retrieve
  private get REQUEST_RETRIEVE_ALL() {
    return `[${this.type}][Request] All`;
  }

  private get REQUEST_RETRIEVE_BY_ID() {
    return `[${this.type}][Request] By ID`;
  }

  private get SELECT_BY_ID() {
    return `[${this.type}][Select] By ID`;
  }

  private get RESPONSE_SUCCESS_RETRIEVE_ALL() {
    return `[${this.type}][Response][Success] All`;
  }

  private get RESPONSE_FAIL_RETRIEVE_ALL() {
    return `[${this.type}][Response][Fail] All`;
  }

  private get RESPONSE_SUCCESS_RETRIEVE_BY_ID() {
    return `[${this.type}][Response][Success] By ID`;
  }

  private get RESPONSE_FAIL_RETRIEVE_BY_ID() {
    return `[${this.type}][Response][Fail] By ID`;
  }

  private get PAGE_CHANGE() {
    return `[${this.type}][Request] Paginate`;
  }

  private get PAGE_CHANGE_BY_SEGMENT() {
    return `[${this.type}][Request] Paginate (by Id)`;
  }

  private get UPDATE_FILTER() {
    return `[${this.type}][Request] Update Filter`;
  }

  private get UPDATE_FILTER_FOR_SEGMENT() {
    return `[${this.type}][Request] Update Filter (by paginator Id)`;
  }

  private get RESET_PAGE() {
    return `[${this.type}][Request] Reset Pagination`;
  }

  private get REQUEST_PAGE() {
    return `[${this.type}][Request] Update Page`;
  }

  private get REQUEST_COUNT() {
    return `[${this.type}][Request] Count`;
  }

  private get RESPONSE_SUCCESS_COUNT() {
    return `[${this.type}][Response][Success] Count`;
  }

  private get RESPONSE_FAIL_COUNT() {
    return `[${this.type}][Response][Fail] Count`;
  }

  private get REQUEST_SEARCH() {
    return `[${this.type}][Request] Search`;
  }

  private get RESPONSE_SUCCESS_SEARCH() {
    return `[${this.type}][Response][Success] Search`;
  }

  private get RESPONSE_FAIL_SEARCH() {
    return `[${this.type}][Response][Fail] Search`;
  }

  private get CLEAR_SEGMENT() {
    return `[${this.type}] Clear Pagination`;
  }

  private get CLEAR_SEGMENT_IDS() {
    return `[${this.type}] Clear ID list from pagination entry`;
  }

  private get REQUEST_ALL_FOR_SEGMENT() {
    return `[${this.type}][Request] All Paginated`;
  }

  private get RESPONSE_SUCCESS_ALL_FOR_SEGMENT() {
    return `[${this.type}][Response][Success] All Paginated`;
  }

  private get RESPONSE_FAIL_ALL_FOR_SEGMENT() {
    return `[${this.type}][Response][Fail] All Paginated`;
  }

  private get RESPONSE_SUCCESS_RETRIEVE_BY_ID_CACHED() {
    return `[${this.type}][Response][Success] By ID Cached`;
  }

  // Update
  private get REQUEST_UPDATE() {
    return `[${this.type}][Request] Save`;
  }

  private get RESPONSE_SUCCESS_UPDATE() {
    return `[${this.type}][Response][Success] Save`;
  }

  private get RESPONSE_FAIL_UPDATE() {
    return `[${this.type}][Response][Fail] Save`;
  }

  // Delete
  private get REQUEST_DELETE_BY_ID() {
    return `[${this.type}][Request] Destroy By ID`;
  }

  private get RESPONSE_SUCCESS_DELETE_BY_ID() {
    return `[${this.type}][Response][Success] Destroy By ID`;
  }

  private get RESPONSE_FAIL_DELETE_BY_ID() {
    return `[${this.type}][Response][Fail] Destroy By ID`;
  }

  constructor(public type: string) {}

  RequestRetrieveAllAction = createAction(
    this.REQUEST_RETRIEVE_ALL,
    props<{ skipPagination?: boolean }>()
  );

  RequestRetrieveByIdAction = createAction(
    this.REQUEST_RETRIEVE_BY_ID,
    props<{ id: string; forceRefresh?: boolean }>()
  );

  SelectByIdAction = createAction(this.SELECT_BY_ID, props<{ id: string }>());

  ResponseSuccessRetrieveAllAction = createAction(
    this.RESPONSE_SUCCESS_RETRIEVE_ALL,
    props<{ payload: EntityType[] }>()
  );

  ResponseSuccessRetrieveByIdAction = createAction(
    this.RESPONSE_SUCCESS_RETRIEVE_BY_ID,
    props<{ payload: EntityType }>()
  );

  ResponseSuccessRetrieveCachedByIdAction = createAction(
    this.RESPONSE_SUCCESS_RETRIEVE_BY_ID_CACHED,
    props<{ payload: EntityType }>()
  );

  ResponseFailRetrieveByIdAction = createAction(
    this.RESPONSE_FAIL_RETRIEVE_BY_ID,
    props<{ id: string; error: any }>()
  );

  ResponseFailRetrieveAllAction = createAction(
    this.RESPONSE_FAIL_RETRIEVE_ALL,
    props<{ error: any }>()
  );

  RequestCreateAction = createAction(this.REQUEST_CREATE, props<{ payload: CreateType }>());

  ResponseSuccessCreateAction = createAction(
    this.RESPONSE_SUCCESS_CREATE,
    props<{ payload: EntityType }>()
  );

  ResponseFailCreateAction = createAction(this.RESPONSE_FAIL_CREATE, props<{ error: any }>());

  RequestUpdateAction = createAction(
    this.REQUEST_UPDATE,
    props<{ id: string; data: UpdateType }>()
  );

  ResponseSuccessUpdateAction = createAction(
    this.RESPONSE_SUCCESS_UPDATE,
    props<{ payload: EntityType }>()
  );

  ResponseFailUpdateAction = createAction(
    this.RESPONSE_FAIL_UPDATE,
    props<{ id: string; error: any }>()
  );

  PageChangeAction = createAction(
    this.PAGE_CHANGE,
    props<{ page: { pageIndex: number; pageSize: number; length: number } }>()
  );

  PageChangeBySegmentAction = createAction(
    this.PAGE_CHANGE_BY_SEGMENT,
    props<{ segment: string; page: { pageIndex: number; pageSize: number; length: number } }>()
  );

  ResetPageAction = createAction(this.RESET_PAGE);

  RequestPageAction = createAction(this.REQUEST_PAGE);

  // Set isDefault to true when the passed in filter is the default for this segment.
  UpdateFilterForSegmentAction = createAction(
    this.UPDATE_FILTER_FOR_SEGMENT,
    props<{ segment: string; filter: FilterType; isDefault?: boolean }>(),
  );

  // Set isDefault to true when the passed in filter is the default for this segment.
  UpdateFilterAction = createAction(
    this.UPDATE_FILTER,
    props<{ filter: FilterType; isDefault?: boolean }>(),
  );

  RequestCountAction = createAction(this.REQUEST_COUNT);

  ResponseSuccessCountAction = createAction(
    this.RESPONSE_SUCCESS_COUNT,
    props<{ count: number }>(),
  );

  ResponseFailCountAction = createAction(this.RESPONSE_FAIL_COUNT, props<{ error: any }>());

  RequestSearchAction = createAction(this.REQUEST_SEARCH, props<{ search: string }>());

  ResponseSuccessSearchAction = createAction(
    this.RESPONSE_SUCCESS_SEARCH,
    props<{ payload: EntityType[] }>()
  );

  ResponseFailSearchAction = createAction(this.RESPONSE_FAIL_SEARCH, props<{ error: any }>());

  RequestDeleteByIdAction = createAction(this.REQUEST_DELETE_BY_ID, props<{ id: string }>());

  ResponseSuccessDeleteByIdAction = createAction(
    this.RESPONSE_SUCCESS_DELETE_BY_ID,
    props<{ id: string; payload?: any }>()
  );

  ResponseFailDeleteByIdAction = createAction(
    this.RESPONSE_FAIL_DELETE_BY_ID,
    props<{ id: string; error?: any }>()
  );

  RequestAllForSegmentAction = createAction(
    this.REQUEST_ALL_FOR_SEGMENT,
    props<{ segment: string; filter?: FilterType; clear?: boolean }>()
  );

  ResponseSuccessAllForSegmentAction = createAction(
    this.RESPONSE_SUCCESS_ALL_FOR_SEGMENT,
    props<{ segment: string; filter?: FilterType; items: EntityType[] }>()
  );

  ResponseFailAllForSegmentAction = createAction(
    this.RESPONSE_FAIL_ALL_FOR_SEGMENT,
    props<{ segment: string; error?: any }>()
  );

  ClearSegmentAction = createAction(this.CLEAR_SEGMENT, props<{ segment: string }>());

  ClearSegmentIdsAction = createAction(this.CLEAR_SEGMENT_IDS, props<{ segment: string }>());
}
